/**
 * useMapLayerClick Hook
 *
 * Shared click primitive for symbol layers attached to the unified
 * `map-cluster-source`. Wires a MapLibre `click` listener on the given
 * `layerId`, applies the global interaction-suppression guard, extracts the
 * feature's `shortId` and Point coordinates, then opens the corresponding
 * artifact overlay (`/map/<singular>/:itemId`) and zooms the map to the
 * anchor point.
 *
 * Consumed by:
 * - `NoteSymbolLayer` (layer: `NOTE_LAYER_IDS.MARKER_PIN`, artifact: `notes`)
 * - `useObstructionLayerInteractions` (layer: `OBSTRUCTION_LAYER_IDS.SYMBOL`,
 *   artifact: `obstructions`)
 *
 * Task markers use a richer contract (`useTaskLayerInteractions`) — delayed
 * click, double-click, context menu, cluster expand, mobile touch — so they
 * stay on their own path and do not share this primitive.
 *
 * @module components/map/hooks/useMapLayerClick
 */

import { useEffect, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { MapLayerMouseEvent } from 'maplibre-gl';

import type { ArtifactId } from '@/artifacts/navigation-map';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useDeviceLayout } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { selectIsInteractionSuppressed } from '@/store/selectors';
import { previewZoomBottomPadding } from '@/utils/zoomHelpers';

interface UseMapLayerClickProps {
  mapRef: RefObject<MapRef | null>;
  isMapReady: boolean;
  layerId: string;
  artifactKind: ArtifactId;
}

export function useMapLayerClick({
  mapRef,
  isMapReady,
  layerId,
  artifactKind,
}: UseMapLayerClickProps): void {
  const { openArtifact } = useArtifactNavigation();
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);
  const { isMobile } = useDeviceLayout();

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    const handleClick = (event: MapLayerMouseEvent): void => {
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) return;
      const feature = event.features?.[0];
      if (!feature) return;

      const shortId = feature.properties?.shortId;
      if (typeof shortId !== 'string') return;

      const geom = feature.geometry;
      if (geom.type !== 'Point' || geom.coordinates.length < 2) return;
      const lng = geom.coordinates[0] as number;
      const lat = geom.coordinates[1] as number;

      // Opening a detail panel on mobile triggers preview mode — reserve the
      // bottom padding so the marker lands in the visible area above the panel,
      // not behind it. Mirrors `navigateFromList`.
      openArtifact(artifactKind, 'detail', shortId);
      zoomTo(
        { lat, lng },
        { bottomPadding: previewZoomBottomPadding(isMobile) }
      );
    };

    map.on('click', layerId, handleClick);

    return () => {
      map.off('click', layerId, handleClick);
    };
  }, [
    mapRef,
    isMapReady,
    layerId,
    artifactKind,
    openArtifact,
    zoomTo,
    isMobile,
  ]);
}
